import moment from "moment";
import type { intlFormatPropTypes } from "react-intl";
import { browserHistory } from "react-router";

import { Links } from "../../constants/links.consts";
import { DAY_IN_MS } from "../constants/host-hub-upcoming-panel.consts";
import type {
  HostHubUpcomingBooking,
  HostHubUpcomingBookingGroups,
} from "../types/model/upcoming-booking";

/** SPA navigation to the host calendar. */
export const handleCalendarClick = () => {
  browserHistory.push(Links.Calendar);
};

/**
 * Hard-redirect to a booking inbox route. Inbox lives in `web-2` under a
 * different React Router instance, so `browserHistory.push` won't work here.
 */
export const handleBookingClick = (primaryCtaRoute: string) => {
  window.location.assign(primaryCtaRoute);
};

const startOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const endOfLocalDay = (date: Date) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  );

const addLocalDays = (date: Date, days: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

const getLocalDayDifference = (date: Date, today: Date) =>
  Math.floor(
    (startOfLocalDay(date).getTime() - startOfLocalDay(today).getTime()) /
      DAY_IN_MS,
  );

export const getUpcomingBookingsDateRange = (today = new Date()) => ({
  from: startOfLocalDay(today).toISOString(),
  to: endOfLocalDay(addLocalDays(today, 30)).toISOString(),
});

/** Returns a fresh empty groups object so callers can mutate buckets safely. */
export const createEmptyUpcomingBookingGroups =
  (): HostHubUpcomingBookingGroups => ({
    today: [],
    thisWeek: [],
    thisMonth: [],
  });

/**
 * Sorts upcoming bookings within the next 30 days (local time) and buckets
 * them into `today` (same day), `thisWeek` (within 7 days), and `thisMonth`
 * (8–30 days out). Caps the total number of returned bookings at `limit`.
 */
export const groupUpcomingBookings = ({
  bookings,
  limit,
  today = new Date(),
}: {
  bookings: HostHubUpcomingBooking[];
  limit: number;
  today?: Date;
}): HostHubUpcomingBookingGroups => {
  const sortedBookings = [...bookings]
    // Keep bookings starting today through 30 days from now (local time);
    // earlier or later bookings don't belong to any upcoming bucket.
    .filter(({ startDate }) => {
      const dayDifference = getLocalDayDifference(new Date(startDate), today);
      return dayDifference >= 0 && dayDifference <= 30;
    })
    // Order chronologically so the soonest booking surfaces first.
    .sort(
      (firstBooking, secondBooking) =>
        new Date(firstBooking.startDate).getTime() -
        new Date(secondBooking.startDate).getTime(),
    )
    .slice(0, limit);

  return sortedBookings.reduce((bookingGroups, booking) => {
    const dayDifference = getLocalDayDifference(
      new Date(booking.startDate),
      today,
    );

    if (dayDifference === 0) {
      bookingGroups.today.push(booking);
    } else if (dayDifference <= 7) {
      bookingGroups.thisWeek.push(booking);
    } else {
      bookingGroups.thisMonth.push(booking);
    }

    return bookingGroups;
  }, createEmptyUpcomingBookingGroups());
};

export const formatBookingType = (bookingType: string) =>
  bookingType
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(
      (bookingTypePart) =>
        `${bookingTypePart.charAt(0).toUpperCase()}${bookingTypePart
          .slice(1)
          .toLowerCase()}`,
    )
    .join(" ");

export const formatGuestDisplayName = ({
  firstName,
  lastName,
}: {
  firstName?: string;
  lastName?: string;
}) => {
  const lastInitial = lastName?.charAt(0);

  return [firstName, lastInitial ? `${lastInitial}.` : undefined]
    .filter(Boolean)
    .join(" ");
};

const formatLocalizedClock = (
  date: moment.Moment,
  locale: string,
  includeDayPeriod: boolean,
) => {
  const hourFormat = date.minute() ? "h:mm" : "h";
  const formatString = includeDayPeriod ? `${hourFormat} A` : hourFormat;

  return date.clone().locale(locale).format(formatString);
};

export const formatUpcomingBookingDescription = ({
  endDate,
  intl,
  startDate,
}: {
  endDate?: string;
  intl: Pick<intlFormatPropTypes, "locale">;
  startDate: string;
}): string[] => {
  const startMoment = moment(startDate);
  const endMoment = endDate ? moment(endDate) : undefined;

  if (!startMoment.isValid()) return [];

  const dateText = startMoment.clone().locale(intl.locale).format("MMM D");

  if (!endMoment || !endMoment.isValid()) {
    return [dateText, formatLocalizedClock(startMoment, intl.locale, true)];
  }

  const sameTwelveHourPeriod =
    startMoment.format("A") === endMoment.format("A");

  return [
    dateText,
    `${formatLocalizedClock(
      startMoment,
      intl.locale,
      !sameTwelveHourPeriod,
    )} - ${formatLocalizedClock(endMoment, intl.locale, true)}`,
  ];
};
